import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, NotebookPen, ClipboardList, Bell, Download, LogOut,
  ShieldCheck, Activity, Flame, BadgeCheck, Loader2, BarChart3, FileDown, FileText, Microscope, ArrowRight, Star, MessageSquare, Eye, EyeOff, Search,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";
import AsaLogo from "../components/AsaLogo";
import { useAsaAuth } from "../AuthContext";
import { symptomLabel } from "../data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TEAL = "#0d9488";

function AdminLogin({ onDone }) {
  const { login, logout } = useAsaAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login({ email, password });
      if (u.role !== "admin") { logout(); toast.error("This account is not an administrator."); return; }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const field = "w-full h-11 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488] transition-colors";
  return (
    <div className="min-h-screen bg-[#f4f8f7] flex flex-col items-center justify-center px-4" data-testid="admin-login">
      <AsaLogo size={36} />
      <form onSubmit={submit} className="w-full max-w-[400px] bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mt-8">
        <div className="flex items-center gap-2 text-[#0f766e]"><ShieldCheck size={20} /><h1 className="font-head font-bold text-lg text-slate-800">Foundation Admin</h1></div>
        <p className="text-slate-500 text-sm mt-1">Sign in to view collected patient data.</p>
        <div className="space-y-4 mt-6">
          <input data-testid="admin-email" type="email" className={field} placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="admin-login-submit" disabled={loading} className="w-full h-11 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-50 text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin" />} Sign in
          </button>
        </div>
      </form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tint + "1a", color: tint }}><Icon size={16} /></span>
    </div>
    <p className="font-head font-bold text-3xl text-slate-800 mt-2">{value}</p>
  </div>
);

const TABS = [
  { id: "insights", label: "Research insights", icon: BarChart3 },
  { id: "users", label: "Members", icon: Users },
  { id: "diary", label: "Diary entries", icon: NotebookPen },
  { id: "checker", label: "Checker results", icon: ClipboardList },
  { id: "notifications", label: "Events", icon: Bell },
  { id: "feedback", label: "Feedback", icon: Star },
];

export default function AdminDashboard() {
  const { user, ready, logout } = useAsaAuth();
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("insights");
  const [rows, setRows] = useState([]);
  const [insights, setInsights] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showEmails, setShowEmails] = useState(true);
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAdmin = user && user.role === "admin";

  useEffect(() => { if (isAdmin) setAuthed(true); }, [isAdmin]);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    const q = days ? `?days=${days}` : "";
    setInsights(null);
    axios.get(`${API}/admin/insights${q}`).then((r) => setInsights(r.data)).catch(() => {});
  }, [authed, days]);

  useEffect(() => {
    if (!authed || tab !== "feedback") return;
    setFeedback(null);
    axios.get(`${API}/admin/feedback`).then((r) => setFeedback(r.data)).catch(() => setFeedback({ ratings: [], cancellations: [] }));
  }, [authed, tab]);

  useEffect(() => {
    if (!authed || tab === "insights" || tab === "feedback") return;
    setLoading(true);
    axios.get(`${API}/admin/${tab}`)
      .then((r) => setRows(r.data.users || r.data.entries || r.data.results || r.data.notifications || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tab, authed]);

  const reloadUsers = () => axios.get(`${API}/admin/users`).then((r) => setRows(r.data.users || [])).catch(() => {});

  const grantAccess = async (email) => {
    try {
      const { data } = await axios.post(`${API}/admin/users/grant-access`, { email });
      toast.success(data.message || "Full access granted");
      reloadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant access"); }
  };

  const revokeAccess = async (email) => {
    if (!window.confirm(`Revoke free Pro access for ${email}? They will return to no subscription.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/revoke-access`, { email });
      toast.success(data.message || "Access revoked");
      reloadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke access"); }
  };

  const exportCsv = async (dataset, showEmails = false) => {
    try {
      const res = await axios.get(`${API}/admin/export/${dataset}?anonymise=${showEmails ? 0 : 1}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `asa_${dataset}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const downloadReport = async (format) => {
    try {
      const q = `format=${format}${days ? `&days=${days}` : ""}`;
      const res = await axios.get(`${API}/admin/insights/report?${q}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `asal_research_report.${format}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Research report (${format.toUpperCase()}) downloaded`);
    } catch { toast.error("Report download failed"); }
  };

  const sendWeeklySummary = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/send-weekly-summary`);
      toast.success(`Weekly summary emailed: ${data.new_members} new members, ${data.new_pro} new Pro`);
    } catch { toast.error("Could not send weekly summary"); }
  };

  const markFollowedUp = async (id) => {
    try {
      await axios.post(`${API}/admin/cancellation/followup`, { id });
      setStats((s) => s ? { ...s, cancellations: { ...s.cancellations, open: Math.max(0, (s.cancellations?.open || 1) - 1), recent: (s.cancellations?.recent || []).map((c) => c.id === id ? { ...c, followed_up: true } : c) } } : s);
      toast.success("Marked as followed up");
    } catch { toast.error("Could not update"); }
  };

  if (!ready) return <div className="min-h-screen bg-[#f4f8f7] flex items-center justify-center"><Loader2 className="animate-spin text-[#0d9488]" /></div>;
  if (!authed) return <AdminLogin onDone={() => setAuthed(true)} />;

  const searchQ = search.trim().toLowerCase();
  const displayRows = (tab === "users" && searchQ)
    ? rows.filter((r) => (r.email || "").toLowerCase().includes(searchQ) || (r.name || "").toLowerCase().includes(searchQ))
    : rows;

  const printMembers = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print the member list"); return; }
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const body = displayRows.map((r) => `<tr><td>${esc(r.email)}</td><td>${esc(r.name)}</td><td>${esc(r.tier)}${r.comp_access ? " (FREE PRO)" : ""}</td><td>${r.subscribed ? "Yes" : "No"}</td><td>${esc((r.created_at || "").slice(0, 10))}</td></tr>`).join("");
    w.document.write(`<html><head><title>ASAL Members</title><style>body{font-family:system-ui,-apple-system,sans-serif;padding:28px;color:#0f172a}h1{font-size:18px;margin:0 0 4px}p{color:#64748b;font-size:12px;margin:0 0 16px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #e2e8f0;padding:6px 9px;text-align:left}th{background:#f1f5f9;text-transform:uppercase;font-size:10px;letter-spacing:.04em}@media print{@page{margin:14mm}}</style></head><body><h1>ASAL — Members (${displayRows.length})</h1><p>Generated ${new Date().toLocaleString("en-GB")}${searchQ ? ` · filtered by "${esc(search.trim())}"` : ""}</p><table><thead><tr><th>Email</th><th>Name</th><th>Tier</th><th>Subscribed</th><th>Joined</th></tr></thead><tbody>${body}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 350);
  };

  return (
    <div className="min-h-screen bg-[#f4f8f7]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><AsaLogo size={30} /><span className="text-[13px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-2.5 py-1 rounded-full inline-flex items-center gap-1"><ShieldCheck size={13} /> Admin</span></div>
          <button data-testid="admin-logout" onClick={() => { logout(); setAuthed(false); }} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm font-medium"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="font-head font-bold text-2xl text-slate-800">Collected patient data</h1>
        <p className="text-slate-500 mt-1">Anonymous health data shared with the ASA Foundation.</p>

        <button data-testid="open-research-observatory" onClick={() => navigate("/asa/research-observatory")}
          className="w-full mt-5 flex items-center gap-4 p-5 rounded-2xl bg-[#0b3b38] text-white hover:bg-[#0f4a45] transition-colors text-left">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Microscope size={26} /></div>
          <div className="flex-1">
            <p className="font-head font-bold text-lg">ASAL Research Observatory</p>
            <p className="text-white/75 text-[13px]">Population-level research analytics — aggregated, privacy-protected, no individual identifiers.</p>
          </div>
          <ArrowRight size={22} className="shrink-0" />
        </button>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatCard icon={Users} label="Members" value={stats.users} tint={TEAL} />
            <StatCard icon={BadgeCheck} label="Subscribed" value={stats.subscribed} tint="#22a35a" />
            <StatCard icon={NotebookPen} label="Diary entries" value={stats.diary_entries} tint="#e08a2b" />
            <StatCard icon={ClipboardList} label="Checker results" value={stats.checker_results} tint="#6366f1" />
            <StatCard icon={Flame} label="Episode days" value={stats.flare_days} tint="#e0567a" />
            <StatCard icon={Activity} label="Pro members" value={stats.subscribed_by_tier?.pro ?? 0} tint="#0f766e" />
            <StatCard icon={Bell} label="Events" value={stats.notifications} tint="#64748b" />
          </div>
        )}

        {stats?.revenue && (
          <div className="mt-6 rounded-2xl border border-[#c9e9e4] bg-gradient-to-br from-[#e6f4f2] to-white p-5" data-testid="admin-revenue-panel">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-[#0f766e]" />
              <h3 className="font-head font-bold text-slate-800">Subscription income (estimated)</h3>
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1">Based on {stats.revenue.paying} paying Pro members ({stats.revenue.monthly_members} monthly · {stats.revenue.yearly_members} yearly). {stats.revenue.comp} complimentary member{stats.revenue.comp === 1 ? "" : "s"} excluded. 100% funds the ASA Foundation.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Monthly income</p>
                <p className="text-2xl font-head font-bold text-[#0d9488] mt-1" data-testid="admin-mrr">£{stats.revenue.mrr.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-[11px] text-slate-400">per month (MRR)</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Annual income</p>
                <p className="text-2xl font-head font-bold text-[#0f766e] mt-1" data-testid="admin-arr">£{stats.revenue.arr.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-[11px] text-slate-400">per year (ARR)</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Monthly plans</p>
                <p className="text-2xl font-head font-bold text-slate-700 mt-1">{stats.revenue.monthly_members}</p>
                <p className="text-[11px] text-slate-400">£{stats.revenue.monthly_price}/mo each</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Yearly plans</p>
                <p className="text-2xl font-head font-bold text-slate-700 mt-1">{stats.revenue.yearly_members}</p>
                <p className="text-[11px] text-slate-400">£{stats.revenue.yearly_price}/yr each</p>
              </div>
            </div>
          </div>
        )}

        {stats?.cancellations?.recent?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-[#f0c9a8] bg-[#fdf6ee] p-5" data-testid="admin-cancellations-panel">
            <div className="flex items-center gap-2">
              <Flame size={18} className="text-[#c2571a]" />
              <h3 className="font-head font-bold text-slate-800">Cancellations to follow up</h3>
              {stats.cancellations.open > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[12px] font-bold text-white bg-[#e0567a] rounded-full" data-testid="admin-cancel-count">{stats.cancellations.open}</span>
              )}
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1">Recent ASAL Pro cancellations and the reason each member gave. Mark as followed up once you've reached out or noted it.</p>
            <div className="mt-4 space-y-2">
              {stats.cancellations.recent.map((c) => (
                <div key={c.id} data-testid="admin-cancel-row" className={`flex flex-wrap items-center gap-2 rounded-xl border p-3 ${c.followed_up ? "border-slate-200 bg-white/60 opacity-60" : "border-[#f0c9a8] bg-white"}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-semibold text-slate-800 truncate">{c.user_name || "Member"} <span className="font-normal text-slate-500">· {c.user_email || "—"}</span></p>
                    <p className="text-[12.5px] text-slate-600 mt-0.5"><span className="font-medium text-[#c2571a]">{c.reason || "Not given"}</span>{c.reason_detail ? ` — ${c.reason_detail}` : ""}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Cancelled {(c.created_at || "").slice(0, 10)}{c.access_until ? ` · Pro until ${String(c.access_until).slice(0, 10)}` : ""}</p>
                  </div>
                  {c.followed_up ? (
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#22a35a]"><BadgeCheck size={14} /> Followed up</span>
                  ) : (
                    <button data-testid="admin-cancel-followup" onClick={() => markFollowedUp(c.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border border-slate-200 text-slate-600 hover:border-[#0d9488] shrink-0">Mark followed up</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-8 mb-4">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#0d9488] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
          {(tab === "users" || tab === "diary" || tab === "checker" || tab === "notifications" || tab === "feedback") && (
            <div className="ml-auto flex items-center gap-2">
              {tab === "users" && (
                <div className="relative">
                  <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="admin-member-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email or name" className="pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:border-[#0d9488] outline-none w-52" />
                </div>
              )}
              <button data-testid="toggle-show-emails" onClick={() => setShowEmails((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold border transition-colors ${showEmails ? "border-[#0d9488] text-[#0f766e] bg-[#e6f4f2]" : "border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>
                {showEmails ? <Eye size={15} /> : <EyeOff size={15} />} {showEmails ? "Emails visible" : "Anonymised"}
              </button>
              {(tab === "users" || tab === "diary" || tab === "checker") && (
                <button data-testid="admin-export" onClick={() => exportCsv(tab, tab === "users" ? true : showEmails)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:border-[#0d9488]"><Download size={15} /> {tab === "users" ? "Export CSV (emails)" : "Export CSV"}</button>
              )}
              {tab === "users" && (
                <button data-testid="admin-print" onClick={printMembers} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:border-[#0d9488]"><FileText size={15} /> Print</button>
              )}
              {tab === "users" && (
                <button data-testid="admin-weekly-summary" onClick={sendWeeklySummary} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:border-[#0d9488]"><Bell size={15} /> Email summary</button>
              )}
            </div>
          )}
        </div>

        {tab === "insights" ? (
          <ResearchInsights data={insights} days={days} setDays={setDays} onDownload={downloadReport} />
        ) : tab === "feedback" ? (
          <FeedbackPanel data={feedback} showEmails={showEmails} />
        ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div>
          ) : displayRows.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm" data-testid="admin-no-rows">{tab === "users" && searchQ ? `No members match "${search.trim()}".` : "No data yet."}</div>
          ) : (
            <div className="overflow-x-auto">
              <DataTable tab={tab} rows={displayRows} onGrant={grantAccess} onRevoke={revokeAccess} showEmails={showEmails} />
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  );
}

function FeedbackPanel({ data, showEmails }) {
  const [starFilter, setStarFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  if (!data) return <div className="p-12 text-center bg-white rounded-2xl border border-slate-200"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div>;
  const allRatings = data.ratings || [];
  const allCancellations = data.cancellations || [];
  const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace("T", " ") : "—");

  const cutoff = dayFilter === "all" ? null : Date.now() - Number(dayFilter) * 86400000;
  const inRange = (iso) => (cutoff == null ? true : (iso && new Date(iso).getTime() >= cutoff));
  const ratings = allRatings.filter((r) => inRange(r.created_at)).filter((r) => starFilter === "all" || String(r.stars) === starFilter);
  const cancellations = allCancellations.filter((c) => inRange(c.created_at));
  const filteredAvg = ratings.length ? (ratings.reduce((s, r) => s + (r.stars || 0), 0) / ratings.length).toFixed(2) : 0;
  const filtered = dayFilter !== "all" || starFilter !== "all";

  const downloadCsv = () => {
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const header = ["Type", "Member", "Email", "Stars", "Comment", "Reason", "Details", "Access until", "Date"];
    const rows = [
      ...ratings.map((r) => ["Rating", r.user_name, r.user_email, r.stars, r.comment, "", "", "", r.created_at]),
      ...cancellations.map((c) => ["Cancellation", c.user_name, c.user_email, "", "", c.reason, c.reason_detail, c.access_until, c.created_at]),
    ];
    const csv = [header, ...rows].map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asal-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selCls = "h-9 px-2.5 rounded-lg border border-slate-200 text-[13px] text-slate-600 bg-white outline-none focus:border-[#0d9488]";

  return (
    <div className="space-y-5" data-testid="admin-feedback">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-head font-semibold text-slate-800">Member feedback</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={dayFilter} onChange={(e) => setDayFilter(e.target.value)} data-testid="feedback-filter-days" className={selCls}>
            <option value="all">All time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select value={starFilter} onChange={(e) => setStarFilter(e.target.value)} data-testid="feedback-filter-stars" className={selCls}>
            <option value="all">All ratings</option>
            <option value="5">5 stars</option>
            <option value="4">4 stars</option>
            <option value="3">3 stars</option>
            <option value="2">2 stars</option>
            <option value="1">1 star</option>
          </select>
          <button onClick={downloadCsv} data-testid="feedback-export-csv" disabled={!ratings.length && !cancellations.length}
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 text-slate-600 font-semibold text-[13px] hover:bg-slate-50 disabled:opacity-50 transition-colors">
            <FileDown size={15} /> Download CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="feedback-avg-rating">
          <p className="text-[12px] text-slate-400 font-semibold uppercase tracking-wide">Average rating</p>
          <p className="text-3xl font-head font-bold text-slate-800 mt-1 flex items-center gap-1.5"><Star size={22} className="text-amber-400 fill-amber-400" /> {filteredAvg || 0}<span className="text-base font-normal text-slate-400">/5</span></p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-[12px] text-slate-400 font-semibold uppercase tracking-wide">Ratings{filtered ? " (filtered)" : ""}</p>
          <p className="text-3xl font-head font-bold text-slate-800 mt-1">{ratings.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-[12px] text-slate-400 font-semibold uppercase tracking-wide">Cancellations{filtered ? " (filtered)" : ""}</p>
          <p className="text-3xl font-head font-bold text-slate-800 mt-1">{cancellations.length}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-[12px] text-slate-400 font-semibold uppercase tracking-wide">Reminders sent</p>
          <p className="text-3xl font-head font-bold text-slate-800 mt-1">{data.reminders_sent || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="feedback-recovered">
          <p className="text-[12px] text-slate-400 font-semibold uppercase tracking-wide">Renewals recovered</p>
          <p className="text-3xl font-head font-bold text-[#0d9488] mt-1">{data.renewals_recovered || 0}<span className="text-base font-normal text-slate-400"> · {data.recovery_rate || 0}%</span></p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><Star size={16} className="text-amber-400" /><h3 className="font-head font-semibold text-slate-800">Ratings</h3><span className="text-[12px] text-slate-400">({ratings.length})</span></div>
        {ratings.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">No ratings match these filters.</div> : (
          <div className="overflow-x-auto"><table className="w-full" data-testid="admin-table-ratings">
            <thead><tr className="text-left text-[12px] text-slate-400 border-b border-slate-100"><th className="px-5 py-2.5">Member</th><th className="px-5 py-2.5">Rating</th><th className="px-5 py-2.5">Comment</th><th className="px-5 py-2.5">When</th></tr></thead>
            <tbody>{ratings.map((r, i) => (
              <tr key={i} className="border-b border-slate-50 text-[13px]">
                <td className="px-5 py-2.5 text-slate-700">{showEmails ? (r.user_name || "—") : "—"}<br /><span className="text-[11px] text-slate-400">{maskEmail(r.user_email, showEmails)}</span></td>
                <td className="px-5 py-2.5 whitespace-nowrap text-amber-500">{"★".repeat(r.stars || 0)}<span className="text-slate-300">{"★".repeat(5 - (r.stars || 0))}</span></td>
                <td className="px-5 py-2.5 text-slate-600 max-w-[320px]">{r.comment || <span className="text-slate-300">—</span>}</td>
                <td className="px-5 py-2.5 text-slate-400 whitespace-nowrap">{fmt(r.created_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><MessageSquare size={16} className="text-rose-400" /><h3 className="font-head font-semibold text-slate-800">Cancellation reasons</h3><span className="text-[12px] text-slate-400">({cancellations.length})</span></div>
        {cancellations.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">No cancellations match these filters.</div> : (
          <div className="overflow-x-auto"><table className="w-full" data-testid="admin-table-cancellations">
            <thead><tr className="text-left text-[12px] text-slate-400 border-b border-slate-100"><th className="px-5 py-2.5">Member</th><th className="px-5 py-2.5">Reason</th><th className="px-5 py-2.5">Details</th><th className="px-5 py-2.5">When</th></tr></thead>
            <tbody>{cancellations.map((c, i) => (
              <tr key={i} className="border-b border-slate-50 text-[13px]">
                <td className="px-5 py-2.5 text-slate-700">{showEmails ? (c.user_name || "—") : "—"}<br /><span className="text-[11px] text-slate-400">{maskEmail(c.user_email, showEmails)}</span></td>
                <td className="px-5 py-2.5 text-slate-700 font-medium">{c.reason || "—"}</td>
                <td className="px-5 py-2.5 text-slate-600 max-w-[320px]">{c.reason_detail || <span className="text-slate-300">—</span>}</td>
                <td className="px-5 py-2.5 text-slate-400 whitespace-nowrap">{fmt(c.created_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// Anonymise member emails in the admin view. Off by default; toggle reveals real emails.
// When hidden, shows a stable pseudonym so rows can still be told apart.
const maskEmail = (email, show) => {
  if (show) return email || "—";
  if (!email) return "—";
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  return "Member " + h.toString(36).slice(0, 5).toUpperCase();
};

function DataTable({ tab, rows, onGrant, onRevoke, showEmails }) {
  const th = "text-left text-[12px] font-semibold text-slate-500 uppercase tracking-wide px-4 py-3 whitespace-nowrap";
  const td = "px-4 py-3 text-[13px] text-slate-700 border-t border-slate-100 align-top";

  if (tab === "users") {
    return (
      <table className="w-full" data-testid="admin-table-users">
        <thead className="bg-slate-50"><tr><th className={th}>Email</th><th className={th}>Name</th><th className={th}>Tier</th><th className={th}>Subscribed</th><th className={th}>Diary</th><th className={th}>Checks</th><th className={th}>Joined</th><th className={th}>Access</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id}><td className={td}>{maskEmail(r.email, showEmails)}</td><td className={td}>{showEmails ? r.name : "—"}</td>
            <td className={td}><span className="capitalize">{r.tier}</span>{r.comp_access && <span className="ml-1.5 text-[10px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-1.5 py-0.5 rounded-full">FREE PRO</span>}</td>
            <td className={td}>{r.subscribed ? <span className="text-[#22a35a] font-semibold">Yes</span> : <span className="text-slate-400">No</span>}</td>
            <td className={td}>{r.diary_count}</td><td className={td}>{r.checker_count}</td><td className={td}>{(r.created_at || "").slice(0, 10)}</td>
            <td className={td}>
              {r.comp_access ? (
                <button data-testid={`revoke-access-${r.email}`} onClick={() => onRevoke(r.email)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#e0567a] border border-[#f3c2ce] hover:bg-[#fdeef2] px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap">Revoke</button>
              ) : (
                <button data-testid={`grant-access-${r.email}`} onClick={() => onGrant(r.email)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-white bg-[#0d9488] hover:bg-[#0f766e] px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap">Grant full access</button>
              )}
            </td></tr>
        ))}</tbody>
      </table>
    );
  }
  if (tab === "diary") {
    return (
      <table className="w-full" data-testid="admin-table-diary">
        <thead className="bg-slate-50"><tr><th className={th}>Member</th><th className={th}>Date</th><th className={th}>Severity</th><th className={th}>Episode</th><th className={th}>Symptoms</th><th className={th}>Notes</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id}><td className={td}>{maskEmail(r.user_email, showEmails)}</td><td className={td}>{r.date}</td><td className={td}>{r.severity}/5</td>
            <td className={td}>{r.flare ? <span className="text-[#e0567a] font-semibold">Yes</span> : "—"}</td>
            <td className={td}>{(r.symptoms || []).join(", ")}</td><td className={td + " max-w-[240px]"}>{r.notes || "—"}</td></tr>
        ))}</tbody>
      </table>
    );
  }
  if (tab === "checker") {
    return (
      <table className="w-full" data-testid="admin-table-checker">
        <thead className="bg-slate-50"><tr><th className={th}>Member</th><th className={th}>Overall</th><th className={th}>Symptoms</th><th className={th}>Family</th><th className={th}>Medication</th><th className={th}>When</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id}><td className={td}>{maskEmail(r.user_email, showEmails)}</td><td className={td}>{r.overall}%</td><td className={td + " max-w-[220px]"}>{(r.symptoms || []).join(", ")}</td>
            <td className={td}>{r.family_degree}</td><td className={td}>{r.medications || "—"}</td><td className={td}>{(r.created_at || "").slice(0, 10)}</td></tr>
        ))}</tbody>
      </table>
    );
  }
  return (
    <table className="w-full" data-testid="admin-table-notifications">
      <thead className="bg-slate-50"><tr><th className={th}>Type</th><th className={th}>Patient</th><th className={th}>Summary</th><th className={th}>Emailed</th><th className={th}>When</th></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={`${r.type}-${r.created_at}-${i}`}><td className={td}><span className="capitalize font-medium">{r.type}</span></td><td className={td}>{maskEmail(r.email, showEmails)}</td>
          <td className={td + " max-w-[360px]"}>{r.summary}</td><td className={td}>{r.email_sent ? "Yes" : <span className="text-slate-400">No ({r.reason})</span>}</td>
          <td className={td}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</td></tr>
      ))}</tbody>
    </table>
  );
}


const SEV_COLORS = ["#22a35a", "#84c318", "#e08a2b", "#f2683c", "#e0567a"];
const FAM_LABELS = { none: "No family history", first: "1st degree", second: "2nd degree", multiple: "Multiple" };
const PIE_COLORS = ["#0d9488", "#e08a2b", "#6366f1", "#e0567a", "#22a35a", "#64748b"];

const ChartCard = ({ title, subtitle, children, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <h3 className="font-head font-semibold text-slate-800">{title}</h3>
    {subtitle && <p className="text-[12.5px] text-slate-400 mb-2">{subtitle}</p>}
    {children}
  </div>
);

function ResearchInsights({ data, days, setDays, onDownload }) {
  const RANGES = [{ id: null, label: "All time" }, { id: 30, label: "Last 30 days" }, { id: 90, label: "Last 90 days" }];
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-3 mb-1" data-testid="insights-controls">
      <div className="flex items-center gap-1 bg-white border border-slate-200 p-1 rounded-lg">
        {RANGES.map((r) => (
          <button key={String(r.id)} data-testid={`insights-range-${r.id ?? "all"}`} onClick={() => setDays(r.id)}
            className={`px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${days === r.id ? "bg-[#0d9488] text-white" : "text-slate-500 hover:text-slate-700"}`}>{r.label}</button>
        ))}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <button data-testid="download-report-pdf" onClick={() => onDownload("pdf")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#0d9488] hover:bg-[#0f766e] text-white transition-colors"><FileDown size={15} /> Download report (PDF)</button>
        <button data-testid="download-report-csv" onClick={() => onDownload("csv")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:border-[#0d9488] transition-colors"><FileText size={15} /> CSV</button>
      </div>
    </div>
  );

  if (!data) return <div className="space-y-4"><Controls /><div className="bg-white rounded-2xl border border-slate-200 p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div></div>;

  const symptomData = (data.top_symptoms || []).map((s) => ({ name: symptomLabel(s.id), count: s.count }));
  const conditionData = (data.top_conditions || []).map((c) => ({ name: c.name, score: c.avg_score }));
  const sevData = Object.entries(data.severity_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const famData = Object.entries(data.family_history || {}).map(([k, v]) => ({ name: FAM_LABELS[k] || k, value: v }));
  const hasData = (data.total_diary || 0) + (data.total_checks || 0) > 0;

  if (!hasData) {
    return <div className="space-y-4"><Controls /><div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 text-sm" data-testid="insights-empty">No submissions in this period — insights will appear as members log symptoms and run checks.</div></div>;
  }

  return (
    <div className="space-y-5" data-testid="research-insights">
      <Controls />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Activity} label="Avg severity" value={`${data.avg_severity}/5`} tint="#e08a2b" />
        <StatCard icon={Flame} label="Episode rate" value={`${data.flare_rate}%`} tint="#e0567a" />
        <StatCard icon={NotebookPen} label="Diary logs" value={data.total_diary} tint={TEAL} />
        <StatCard icon={ClipboardList} label="Checks run" value={data.total_checks} tint="#6366f1" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <ChartCard title="Most reported symptoms" subtitle="Across all members' diaries & checks" testid="chart-symptoms">
          <ResponsiveContainer width="100%" height={Math.max(240, symptomData.length * 34)}>
            <BarChart data={symptomData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} fill={TEAL} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Condition likelihood trends" subtitle="Average checker score per condition (%)" testid="chart-conditions">
          <ResponsiveContainer width="100%" height={Math.max(240, conditionData.length * 34)}>
            <BarChart data={conditionData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} formatter={(v) => `${v}%`} />
              <Bar dataKey="score" radius={[0, 4, 4, 0]} fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Symptom severity distribution" subtitle="How members rate their days (1–5)" testid="chart-severity">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sevData} margin={{ left: -20, right: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {sevData.map((e, i) => <Cell key={e.name} fill={SEV_COLORS[i] || TEAL} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Family history of autoimmunity" subtitle="Reported in symptom checks" testid="chart-family">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={famData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 11 }}>
                {famData.map((e, i) => <Cell key={e.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <p className="text-[12px] text-slate-400">All figures are de-identified and aggregated across consenting members — safe to share with research partners.</p>
    </div>
  );
}
